import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useSubscription } from '@apollo/client';
import { Registration, RegistrationPartyChanged } from '../API';
import { individualAttendanceEnabled, partyNotificationsEnabled } from '../constants/attendance';
import { getParty, listParties, PARTY_CHANGED } from '../graphql/partyAttendance';
import { mergeParty } from '../utils/partyAttendance';

export function usePartyRefresh(eventId: string | undefined, registrations: Registration[], setRegistrations: Dispatch<SetStateAction<Registration[]>>) {
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const activeEvent = useRef(eventId);
  activeEvent.current = eventId;
  const latest = useRef(registrations);
  latest.current = registrations;
  const deleted = useRef(new Map<string, number>());
  const running = useRef(false);
  const generation = useRef(0);
  useEffect(() => { generation.current++; deleted.current.clear(); }, [eventId]);
  const refreshParty = useCallback(async (registrationId: string) => {
    if (!eventId || activeEvent.current !== eventId) return null;
    const current = generation.current;
    const party = await getParty(eventId, registrationId);
    if (current !== generation.current) return null;
    if (!party) deleted.current.set(registrationId, Number.MAX_SAFE_INTEGER);
    setRegistrations(previous => {
      if (!party) return previous.filter(p => p.registrationId !== registrationId);
      if ((deleted.current.get(registrationId) ?? 0) >= (party.partyVersion ?? 0) && deleted.current.has(registrationId)) return previous;
      return mergeParty(previous, party);
    });
    return party;
  }, [eventId, setRegistrations]);
  const refreshAll = useCallback(async () => {
    if (!individualAttendanceEnabled || !eventId || running.current) return;
    running.current = true;
    const current = generation.current;
    try {
      const before = latest.current;
      const parties = await listParties(eventId);
      if (current !== generation.current) return;
      // Lists are coherent per party, not an atomic event snapshot. Confirm missing rows individually.
      setRegistrations(previous => parties.reduce((result, party) => {
        if ((deleted.current.get(party.registrationId) ?? 0) >= (party.partyVersion ?? 0) && deleted.current.has(party.registrationId)) return result;
        return mergeParty(result, party);
      }, previous));
      const present = new Set(parties.map(p => p.registrationId));
      // Confirm deletions with a strong point read; an event list spans multiple snapshots.
      for (const missing of before.filter(p => !present.has(p.registrationId))) {
        if (current !== generation.current) return;
        await refreshParty(missing.registrationId);
      }
      setRefreshError(null);
    } catch { if (current === generation.current) setRefreshError('Unable to refresh attendance. Check your connection and sign-in, then retry.'); }
    finally { running.current = false; }
  }, [eventId, setRegistrations, refreshParty]);
  const { data, error } = useSubscription<{ onRegistrationPartyChanged: RegistrationPartyChanged }>(PARTY_CHANGED, {
    variables: { eventId }, skip: !partyNotificationsEnabled || !eventId,
  });
  useEffect(() => {
    const notice = data?.onRegistrationPartyChanged;
    if (!notice || notice.eventId !== eventId) return;
    if (notice.deleted) {
      deleted.current.set(notice.registrationId, Math.max(deleted.current.get(notice.registrationId) ?? 0, notice.partyVersion));
      setRegistrations(previous => previous.filter(p => p.registrationId !== notice.registrationId || (p.partyVersion ?? 0) > notice.partyVersion));
    } else {
      void refreshParty(notice.registrationId).catch(() => setRefreshError('A party changed but could not be refreshed. Retry refresh.'));
    }
  }, [data, eventId, refreshParty, setRegistrations]);
  useEffect(() => {
    if (!individualAttendanceEnabled || !eventId) return;
    void refreshAll();
    const refresh = () => { if (document.visibilityState !== 'hidden') void refreshAll(); };
    const timer = window.setInterval(refresh, 15000);
    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);
    const effectGeneration = generation.current;
    return () => { window.clearInterval(timer); window.removeEventListener('focus', refresh); window.removeEventListener('online', refresh); generation.current = effectGeneration + 1; };
  }, [eventId, refreshAll]);
  return { refreshParty, refreshAll, refreshError: refreshError || (error ? 'Live notifications are unavailable. Attendance refresh continues every 15 seconds.' : null) };
}
